/**
 * Login page component with modern two-section split design (minimalist, no animations).
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Logo } from "../components/brand/Logo";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { forgotPassword } from "../api/endpoints/auth";
import { ForgotPasswordModal } from "../components/auth/ForgotPasswordModal";
import { EmailSentModal } from "../components/auth/EmailSentModal";
import { Cpu, Brain, Sparkles, ArrowRight } from "lucide-react";
import DotField from "../components/DotField";

export const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isEmailSentOpen, setIsEmailSentOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");

  const { login, isAuthenticated, user, isLoading, error, clearError } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && user) {
      const fromState = (location.state as { from?: string })?.from;
      const targetPath = fromState ?? "/dashboard";
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location.state]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!email || !password) {
      setFormError("Please enter both email and password.");
      return;
    }

    try {
      await login({ email, password });
    } catch (err) {}
  };

  const handleSendRecoveryEmail = async (email: string) => {
    try {
      await forgotPassword({ email });
      setRecoveryEmail(email);
      setIsForgotPasswordOpen(false);
      setIsEmailSentOpen(true);
    } catch (err) {
      throw err;
    }
  };

  const displayError = formError || error;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f8fafc] dark:bg-background">
      {/* LEFT COLUMN: Branding & Visuals (lg screens only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-grey-50 dark:bg-grey-900/40 text-grey-900 dark:text-white flex-col justify-between p-16 border-r border-grey-200 dark:border-grey-800/60">
        {/* DotField Background */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-100">
          <DotField
            dotRadius={1.6}
            dotSpacing={20}
            cursorRadius={250}
            cursorForce={0.06}
            glowRadius={0}
            gradientFrom="rgba(56, 80, 160, 0.85)"
            gradientTo="rgba(28, 40, 80, 0.75)"
            glowColor="transparent"
          />
        </div>
        <div className="relative z-10 flex items-center">
          <Logo variant="full" size="2xl" />
        </div>

        {/* Center Visuals & Feature highlights */}
        <div className="relative z-10 my-auto flex flex-col gap-10 max-w-md">
          {/* Header Typography */}
          <div className="flex flex-col gap-3 ml-5">
            <h2 className="text-3xl font-bold text-grey-900 dark:text-white tracking-tight leading-tight">
              Quantum ML & Orchestration Platform.
            </h2>
            <p className="text-grey-600 dark:text-grey-400 text-sm mt-1 leading-relaxed min-w-[500px]">
              QuantumCue is a no-code, end-to-end quantum orchestration platform
              for building, training, benchmarking, and deploying quantum
              machine learning models across leading quantum providers.
            </p>
          </div>

          {/* Minimalist Feature Highlight List */}
          <div className="flex flex-col gap-4 mt-4 ml-5">
            <div className="bg-white/90 dark:bg-grey-900/90 backdrop-blur-sm rounded-xl p-4 border border-grey-200/60 dark:border-grey-800/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-start gap-4">
              <div className="text-[#3850A0] dark:text-brand-300 flex-shrink-0 mt-1">
                <Cpu size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-grey-800 dark:text-grey-100">
                  Unified Gateway
                </h3>
                <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5 leading-relaxed">
                  Deploy workflows seamlessly across top hardware providers.
                </p>
              </div>
            </div>

            <div className="bg-white/90 dark:bg-grey-900/90 backdrop-blur-sm rounded-xl p-4 border border-grey-200/60 dark:border-grey-800/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-start gap-4">
              <div className="text-[#3850A0] dark:text-brand-300 flex-shrink-0 mt-1">
                <Brain size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-grey-800 dark:text-grey-100">
                  AI-Guided Orchestration
                </h3>
                <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5 leading-relaxed">
                  Interact with your Copilot to optimize your quantum trained
                  machine learning models instantly.
                </p>
              </div>
            </div>

            <div className="bg-white/90 dark:bg-grey-900/90 backdrop-blur-sm rounded-xl p-4 border border-grey-200/60 dark:border-grey-800/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-start gap-4">
              <div className="text-[#3850A0] dark:text-brand-300 flex-shrink-0 mt-1">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-grey-800 dark:text-grey-100">
                  Zero-Code Curation
                </h3>
                <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5 leading-relaxed">
                  Upload, clean, and auto-label datasets without a single line
                  of code.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom brand footer spacing */}
        <div className="relative z-10 text-xs text-grey-400 dark:text-grey-500 ml-5 mt-5">
          © {new Date().getFullYear()} QuantumCue. All rights reserved.
        </div>
      </div>

      {/* RIGHT COLUMN: Form & Sign-In */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-12 md:p-16 lg:w-1/2 bg-white dark:bg-grey-950 min-h-screen">
        <div className="w-full max-w-[400px] flex flex-col justify-between h-full min-h-[500px]">
          <div className="h-6" />

          <div className="my-auto">
            {/* Header text */}
            <div className="mb-8 text-center lg:text-left">
              <h1 className="text-2xl font-bold text-grey-900 dark:text-white tracking-tight">
                Sign in to your account
              </h1>
              <p className="text-sm text-grey-500 dark:text-grey-400 mt-2">
                Enter your credentials to continue
              </p>
            </div>

            {/* Error banner */}
            {displayError && (
              <div
                className="rounded-lg border px-3 py-2.5 mb-5"
                style={{ background: "#FEF2F2", borderColor: "#FECACA" }}
              >
                <p style={{ fontSize: 13, color: "#B91C1C" }}>{displayError}</p>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <Input
                label="Email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                disabled={isLoading}
              />

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-grey-700 dark:text-text-primary text-sm font-semibold">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsForgotPasswordOpen(true)}
                    className="hover:underline transition-colors font-medium"
                    style={{
                      fontSize: 13,
                      color: "#3850A0",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
                <Input
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                className="w-full mt-2 group relative overflow-hidden"
                size="lg"
                isLoading={isLoading}
                variant="luxury"
              >
                <span className="flex items-center justify-center">
                  <span>Sign in</span>
                  <span className="inline-flex items-center transition-all duration-300 transform -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 w-0 group-hover:w-6 overflow-hidden">
                    <ArrowRight size={16} className="ml-1.5" />
                  </span>
                </span>
              </Button>
            </form>

            {/* Divider */}
            <div className="border-t border-grey-100 dark:border-grey-800 my-6" />

            {/* Register invite link */}
            <p className="text-sm text-grey-500 dark:text-grey-400 text-center lg:text-left">
              Have an invite code?{" "}
              <a
                href="/signup"
                className="font-semibold hover:underline transition-colors"
                style={{ color: "#3850A0" }}
              >
                Register here
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
        onSendEmail={handleSendRecoveryEmail}
      />

      <EmailSentModal
        isOpen={isEmailSentOpen}
        onClose={() => setIsEmailSentOpen(false)}
        email={recoveryEmail}
      />
    </div>
  );
};

export default Login;
